import React, { useState, useEffect, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader2, Key, Eye, EyeOff, Copy, CheckCircle2, ArrowRight, AlertCircle } from "lucide-react";
import authService from '../../services/authService';
import { cn } from "@/lib/utils";

interface ApiKeyStepProps {
  apiKey: string;
  customerCd?: string;
  onFormChange: (data: { apiKey: string, customerCd?: string }) => void;
  onNext: () => void;
  onBack?: () => void;
}

const ApiKeyStep: React.FC<ApiKeyStepProps> = ({ apiKey, customerCd: initialCustomerCd, onFormChange, onNext, onBack }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(!apiKey && !initialCustomerCd);
  const [customerCd, setCustomerCd] = useState<string | null>(initialCustomerCd || null);
  const [isValidated, setIsValidated] = useState<boolean>(!!(apiKey && initialCustomerCd));
  
  const [showApiKey, setShowApiKey] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isValidatingPassword, setIsValidatingPassword] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  const stableOnFormChange = useCallback((data: { apiKey: string, customerCd?: string }) => {
    onFormChange(data);
  }, [onFormChange]);

  const validateApiKey = useCallback(async () => {
    if (!apiKey.trim()) {
      setError('Bitte geben Sie einen gültigen API-Schlüssel ein');
      return false;
    }

    try {
      setIsLoading(true);
      const data = await authService.validateTimeglobeKey(apiKey);
      
      if (data && (data.valid === true || data.success === true)) {
        const customerId = data.customer_cd || data.customer_id || 'Validiert';
        setCustomerCd(customerId);
        onFormChange({ apiKey, customerCd: customerId });
        setIsValidated(true);
        return true;
      } else {
        setError('Der TimeGlobe API-Schlüssel ist ungültig.');
        return false;
      }
    } catch (apiError: any) {
      console.error('API-Fehler:', apiError);
      setError(apiError.message ? 'Validierung fehlgeschlagen: ' + apiError.message : 'Verbindung fehlgeschlagen.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [apiKey, onFormChange]);

  useEffect(() => {
    // If we already have the data via props, don't fetch
    if (apiKey && initialCustomerCd) {
      setCustomerCd(initialCustomerCd);
      setIsValidated(true);
      setIsInitialLoading(false);
      return;
    }

    let isMounted = true;
    const fetchExistingKey = async () => {
      try {
        setIsInitialLoading(true);
        const response = await authService.getBusinessTimeglobeKey();

        if (!isMounted) return;

        if (response && response.timeglobe_auth_key) {
          stableOnFormChange({ apiKey: response.timeglobe_auth_key, customerCd: response.customer_cd || response.customer_id });
          
          if (response.customer_id || response.customer_cd) {
            setCustomerCd(response.customer_id || response.customer_cd);
            setIsValidated(true);
          } else if (apiKey) {
            validateApiKey();
          }
        }
      } catch (err) {
        console.error('Error fetching existing TimeGlobe key:', err);
      } finally {
        if (isMounted) setIsInitialLoading(false);
      }
    };

    if (!apiKey) {
      fetchExistingKey();
    } else {
      setIsInitialLoading(false);
    }
    return () => { isMounted = false; };
  }, [apiKey, initialCustomerCd, stableOnFormChange, validateApiKey]);

  const maskApiKey = (key: string) => {
    if (!key) return '';
    return '*'.repeat(key.length);
  };

  const handleApiKeyVisibilityToggle = () => {
    if (showApiKey) {
      setShowApiKey(false);
    } else {
      setPasswordDialogOpen(true);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setPasswordError('Bitte geben Sie Ihr Passwort ein.');
      return;
    }

    setIsValidatingPassword(true);
    setPasswordError(null);
    try {
      const isValid = await authService.validatePassword(password);
      if (isValid) {
        setShowApiKey(true);
        setPasswordDialogOpen(false);
        setPassword('');
      } else {
        setPasswordError('Ungültiges Passwort.');
      }
    } catch (err) {
      console.error('Password validation error:', err);
      setPasswordError('Fehler bei der Prüfung.');
    } finally {
      setIsValidatingPassword(false);
    }
  };

  const handleCopyApiKey = () => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey);
      setCopyFeedback('Kopiert!');
      setTimeout(() => setCopyFeedback(null), 2000);
    }
  };

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isValidated) return;
    onFormChange({ apiKey: e.target.value });
    setError(null);
    setIsValidated(false);
    setCustomerCd(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidated) {
      const isValid = await validateApiKey();
      if (isValid) onNext();
    } else {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      {isInitialLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-strong" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <h2 className="flex items-center text-xl font-semibold text-foreground">
              <Key className="mr-2 h-6 w-6 text-[#1967D2]" />
              TimeGlobe API-Schlüssel
            </h2>
            <p className="text-muted-foreground text-sm">
              Der API-Schlüssel wird benötigt, um Ihre Agenten mit Ihrem TimeGlobe-Konto zu verbinden.
            </p>
          </div>
          
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {isValidated && customerCd && (
            <Alert className="bg-primary/10 border-primary/25">
              <CheckCircle2 className="h-4 w-4 text-primary-strong" />
              <AlertDescription className="text-primary-strong">
                <span className="block font-medium mb-1 text-muted-foreground">Ihr TimeGlobe Kunden-Code:</span>
                <span className="text-lg font-bold text-[#1967D2]">{customerCd}</span>
                <span className="block mt-1 text-xs text-muted-foreground">Bitte bestätigen Sie dies mit "Weiter".</span>
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="apiKey">API-Schlüssel*</Label>
            <div className="relative">
              <div className="absolute left-3 top-2.5 text-muted-foreground">
                <Key className={cn("h-5 w-5", isValidated ? "text-muted-foreground" : "text-[#1967D2]")} />
              </div>
              <Input
                id="apiKey"
                value={showApiKey ? apiKey : maskApiKey(apiKey)}
                onChange={handleApiKeyChange}
                disabled={isLoading || isValidated}
                placeholder="Ihr TimeGlobe API-Schlüssel"
                className={cn(
                  "pl-10 pr-24 h-11",
                  isValidated && "bg-muted text-muted-foreground cursor-not-allowed"
                )}
              />
              <div className="absolute right-2 top-1.5 flex items-center gap-1">
                {apiKey && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={handleApiKeyVisibilityToggle}
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                )}
                {showApiKey && apiKey && (
                   <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary-strong" onClick={handleCopyApiKey}>
                            {copyFeedback ? <CheckCircle2 className="h-4 w-4 text-success-strong" /> : <Copy className="h-4 w-4" />}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Kopieren</TooltipContent>
                      </Tooltip>
                   </TooltipProvider>
                )}
                {isValidated && <CheckCircle2 className="h-6 w-6 text-success-strong ml-1" />}
              </div>
            </div>
          </div>
          
          <div className="flex justify-start pt-4">
            <Button
              type="submit"
              disabled={isLoading || !apiKey.trim()}
              className="bg-[#1967D2] hover:bg-[#1756B6] text-white px-8 h-11 text-base rounded-full shadow-lg shadow-blue-500/20"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Validiere...
                </>
              ) : isValidated ? (
                <>
                  Weiter <ArrowRight className="ml-2 h-4 w-4" />
                </>
              ) : (
                'Validieren'
              )}
            </Button>
          </div>
        </form>
      )}

      {/* Footer */}
      <div className="fixed bottom-0 left-0 w-full flex justify-center items-center py-2 bg-transparent pointer-events-none opacity-60">
        <span className="text-xs text-muted-foreground mr-2">powered by</span>
        <img src="/images/EcomTask_logo.svg" alt="EcomTask" className="h-6 grayscale opacity-60" />
      </div>

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Passwort erforderlich</DialogTitle>
            <DialogDescription>
              Bitte geben Sie Ihr Passwort ein, um den API-Schlüssel anzuzeigen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Passwort</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => {
                   setPassword(e.target.value);
                   setPasswordError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isValidatingPassword) {
                    handlePasswordSubmit();
                  }
                }}
              />
              {passwordError && (
                <p className="text-sm text-destructive-strong flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {passwordError}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handlePasswordSubmit} disabled={isValidatingPassword}>
               {isValidatingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
               Bestätigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApiKeyStep;
